import { TranslateService, TranslateModule } from '@ngx-translate/core';
import {
  Component,
  output,
  input,
  computed,
  AfterViewInit,
  OnDestroy,
  signal,
  Inject,
} from '@angular/core';
import { DOCUMENT, NgClass, NgTemplateOutlet } from '@angular/common';
import { IconComponent } from '../../icons';
import {
  paginate,
  calcTotalPages,
  calcStartIndex,
  calcEndIndex,
  calcPreviousPage,
  calcNextPage,
} from './pagination.utils';
import { IdGeneratorService } from '@lib/fresco';

@Component({
    selector: 'da-pagination',
    templateUrl: `./pagination.component.html`,
    imports: [NgClass, IconComponent, TranslateModule, NgTemplateOutlet]
})
export class PaginationComponent implements AfterViewInit, OnDestroy {
  /** Name of the UI element that is being paginated. ie. `User List` will be `User List Pagination` */
  public ariaLabel = input.required<string>();

  /** Current page number */
  public pageIndex = input<number>(1);

  /** Total number of items */
  public collectionSize = input.required<number>();

  /** Emit selected page number */
  public pageChange = output<number>();

  /** Array of page numbers to display, including ellipsis (maps to -1) for hidden pages  */
  public pages = signal<number[]>([]);

  public totalPages = computed(() =>
    calcTotalPages(this.collectionSize(), this.pageSize)
  );

  public previousPage = computed(() => calcPreviousPage(this.pageIndex()));

  public nextPage = computed(() =>
    calcNextPage(this.pageIndex(), this.totalPages())
  );

  public i18n = computed(() =>
    this.translateService.instant(
      [
        'Pagination_AriaTarget',
        'Pagination_FirstPage',
        'Pagination_PreviousPage',
        'Pagination_NextPage',
        'Pagination_LastPage',
        'Pagination_ResultsOverview',
      ],
      {
        // fromIndex / toIndex / totalCount for Pagination_ResultsOverview only
        fromIndex: calcStartIndex(this.pageIndex(), this.pageSize),
        toIndex: calcEndIndex(
          this.pageIndex(),
          this.pageSize,
          this.collectionSize()
        ),
        totalCount: this.collectionSize(),
        // target for Pagination_AriaTarget only
        target: this.ariaLabel(),
      }
    )
  );

  public id: string;

  private observer: ResizeObserver;

  /** UX wants ALL pagination to be 50 results. Confer w/them before changing */
  private pageSize: number = 50;

  constructor(
    private translateService: TranslateService,
    private idGenerator: IdGeneratorService,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.id = this.idGenerator.generateStringId('da-pagination-');
  }

  public ngAfterViewInit(): void {
    /* Change pagination layout based on screen size */
    this.observer = new ResizeObserver((entries) => {
      const size = entries[0].contentRect.width < 1024 ? 'small' : 'large';
      this.pages.set(paginate(this.pageIndex(), this.totalPages(), size));
    });

    this.observer.observe(this.document.body);
  }

  public ngOnDestroy(): void {
    this.observer?.unobserve(this.document.body);
    this.observer?.disconnect();
  }

  public showPage(event: Event, page: number): void {
    event.preventDefault();
    event.stopPropagation();

    if (page === this.pageIndex()) {
      return;
    }

    this.pageChange.emit(page);
  }
}
